<template>
  <!--
    AuthBrandPanel — the dark indigo-ink marketing panel that sits on the LEFT of
    every auth surface (login / forgot / reset / register), bridging the marketing
    site's language into the app (onboarding slice 2a, mockup Screen A/B left).

    Purely presentational: carries the Yorlin songline tile, the radar rings +
    sweep, a value prop and the "Made in the Cotswolds" foot. Renders NO pre-auth
    data (no live proof card, no counts) — nothing that would leak state before a
    user is signed in. All copy is passed in by the caller so login and each
    registration step can differ.
  -->
  <aside class="auth-brand" aria-hidden="true">
    <div class="auth-rings">
      <svg viewBox="0 0 720 720" xmlns="http://www.w3.org/2000/svg">
        <g fill="none" stroke="rgba(45,212,191,.22)" stroke-width="1">
          <circle cx="360" cy="360" r="100" />
          <circle cx="360" cy="360" r="180" />
          <circle cx="360" cy="360" r="260" />
          <circle cx="360" cy="360" r="340" stroke-dasharray="4 8" />
        </g>
        <g fill="rgba(45,212,191,.85)">
          <circle cx="450" cy="240" r="4" />
          <circle cx="260" cy="450" r="3" />
          <circle cx="510" cy="430" r="3.5" />
        </g>
        <g fill="rgba(229,72,77,.75)">
          <circle cx="300" cy="260" r="4" />
          <circle cx="470" cy="510" r="3" />
        </g>
      </svg>
    </div>
    <div class="auth-sweep"></div>

    <div class="ab-top">
      <span class="brand-tile">
        <svg viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <linearGradient id="abBrandMark" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0" stop-color="#4F46E5"/>
              <stop offset="1" stop-color="#0EA5A4"/>
            </linearGradient>
          </defs>
          <rect width="64" height="64" rx="14" fill="url(#abBrandMark)"/>
          <path d="M12 45 L17 39 L22 42 L27 36 L32 38 L37 32 L42 35 L47 29"
                fill="none" stroke="#fff" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M47 29 L52 22.4" fill="none" stroke="#FFCC33" stroke-width="3" stroke-linecap="round"/>
        </svg>
      </span>
      <!-- Wordmark drawn as SVG: dotless "i" (U+0131) letterform whose ONLY
           tittle is the brand-yellow (#FFCC33) dot — never a white dot underneath
           (Toby: one dot, the yellow one). Letterforms inherit currentColor (white
           on this dark panel); only the tittle is fixed yellow. Accessible name is
           aria-label="Yorlin" so the dotless glyph is never read as text. -->
      <svg class="brand-word" viewBox="0 0 74 40" xmlns="http://www.w3.org/2000/svg"
           role="img" aria-label="Yorlin">
        <text x="0" y="30" fill="currentColor" font-weight="600" font-size="30" letter-spacing="-0.4"
              font-family="-apple-system,'Segoe UI',Roboto,'Inter',Helvetica,Arial,sans-serif">yorl<tspan>&#305;</tspan>n</text>
        <circle cx="54" cy="8" r="3.2" fill="#FFCC33" />
      </svg>
    </div>

    <div class="ab-mid">
      <span class="ab-kick">{{ kick }}</span>
      <!-- heading may carry a single <em> for the teal-accented phrase -->
      <h2 v-html="heading"></h2>
      <p class="ab-sub">{{ sub }}</p>
      <p v-if="bullets" class="ab-sub ab-bullets">{{ bullets }}</p>
    </div>

    <div class="ab-foot"><i></i>{{ foot }}</div>
  </aside>
</template>

<script>
/**
 * Static marketing panel — see template comment. No API, no store, no data.
 */
export default {
  name: 'AuthBrandPanel',
  props: {
    kick: { type: String, default: 'Welcome back' },
    heading: {
      type: String,
      default: "Your pipelines keep <em>working while you're away</em>.",
    },
    sub: {
      type: String,
      default: 'Sign in to see what your pipelines found, who opened, and who replied.',
    },
    bullets: { type: String, default: '' },
    foot: { type: String, default: 'Made in the Cotswolds · Companies House native' },
  },
};
</script>

<style scoped>
/* Marketing (indigo-ink dark) tokens — local to this panel, mirroring the
   onboarding-auth mockup so the panel reads as continuous with the site. */
.auth-brand {
  --dk-ground: #0B0A2A;
  --dk-text: #EDEDFA;
  --dk-soft: #A5A3CF;
  --teal-bright: #2DD4BF;
  --brand: #4F46E5;
  --indigo-deep: #3730C4;
  --teal: #14B8A6;

  position: relative;
  overflow: hidden;
  color: var(--dk-text);
  background:
    radial-gradient(900px 520px at 82% -12%, rgba(79, 70, 229, .42), transparent 60%),
    radial-gradient(620px 460px at 8% 116%, rgba(20, 184, 166, .16), transparent 60%),
    var(--dk-ground);
  padding: 40px 42px;
  display: flex;
  flex-direction: column;
  min-height: 100%;
  font-family: 'Inter', system-ui, -apple-system, 'Segoe UI', sans-serif;
}

.auth-rings {
  position: absolute;
  inset: 0;
  pointer-events: none;
  opacity: .6;
}
.auth-rings svg {
  position: absolute;
  right: -220px;
  top: -140px;
  width: 720px;
  height: 720px;
}
.auth-sweep {
  position: absolute;
  right: -220px;
  top: -140px;
  width: 720px;
  height: 720px;
  border-radius: 50%;
  background: conic-gradient(from 0deg, rgba(45, 212, 191, .14) 0deg, transparent 70deg, transparent 360deg);
  animation: authsweep 9s linear infinite;
  pointer-events: none;
  mix-blend-mode: screen;
}
@keyframes authsweep { to { transform: rotate(360deg); } }
@media (prefers-reduced-motion: reduce) {
  .auth-sweep { animation: none; }
}

.ab-top {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 11px;
}
.brand-tile {
  width: 36px;
  height: 36px;
  border-radius: 9px;
  display: block;
  overflow: hidden;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, .25), 0 2px 8px rgba(79, 70, 229, .4);
}
.brand-tile svg {
  display: block;
  width: 100%;
  height: 100%;
}
.brand-word {
  /* SVG wordmark — height drives size; `color` feeds the letterforms'
     currentColor (white), while the tittle stays fixed #FFCC33 in the markup. */
  display: block;
  height: 22px;
  width: auto;
  color: #fff;
}

.ab-mid {
  position: relative;
  z-index: 1;
  margin-top: auto;
  margin-bottom: auto;
  padding: 26px 0;
  /* Centre the marketing text block within the panel (Toby feedback: it was
     left-aligned near the panel edge). Vertical placement stays centred via the
     auto top/bottom margins above. */
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}
.ab-kick {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: .18em;
  text-transform: uppercase;
  color: var(--teal-bright);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin-bottom: 16px;
}
.ab-kick::before {
  content: "";
  width: 22px;
  height: 2px;
  background: var(--teal-bright);
  border-radius: 2px;
}
.ab-mid :deep(h2) {
  font-size: clamp(24px, 2.4vw, 31px);
  font-weight: 800;
  letter-spacing: -.035em;
  line-height: 1.12;
  color: #fff;
  max-width: 18ch;
  margin-left: auto;
  margin-right: auto;
}
.ab-mid :deep(h2 em) {
  font-style: normal;
  color: var(--teal-bright);
}
.ab-sub {
  font-size: 14.5px;
  color: var(--dk-soft);
  max-width: 42ch;
  margin-top: 16px;
  margin-left: auto;
  margin-right: auto;
}
.ab-bullets {
  margin-top: 14px;
  font-size: 13px;
}

.ab-foot {
  position: relative;
  z-index: 1;
  font-size: 12px;
  color: var(--dk-soft);
  margin-top: 24px;
  display: flex;
  align-items: center;
  gap: 8px;
}
.ab-foot i {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: var(--teal);
  flex: none;
}
</style>
